import { gltfLoaderComponentType, makeGLTFLoaderComponent } from 'src/sdk-components';
import { TourActionType, TourObject, VSInteractionType } from 'src/sdk-components/VSObject';
import { SDKConfig } from 'src/service/types';
import { getVersion, meshMaterialChange } from 'src/utils/all';
import { TransformationMode } from 'src/service/scene/scene-nodes/TransformControls/data';
import { MpSdk } from 'src/types';
import { LoadingState, TSceneNode } from 'src/service/scene/types';
import Scene from '../../scene';
import { objLoaderComponentType, SceneMode } from './types';
import logger from 'src/logger';
import { NodeEventsTypes } from '../../enums';
import { CachedGeometry } from 'src/helpers/geometry';

class SceneObject extends Scene {
  readonly mpSDK: any;

  public effectComposer: any;

  public three: any;

  assets = new Array<[any, any[]]>();

  constructor(
    mpSDK: MpSdk,
    private config: SDKConfig,
    private transformControls: any,
  ) {
    super(mpSDK);
    this.mpSDK = mpSDK;
  }

  async init() {
    await this.mpSDK.Scene.register(gltfLoaderComponentType, makeGLTFLoaderComponent);
  }

  async create(data: TourObject, enableCollider?: boolean): Promise<TSceneNode> {
    //@ts-ignore
    await this.mpSDK.Scene.configure((renderer, three, effectComposer) => {
      if (effectComposer) this.effectComposer = effectComposer;
      this.three = three;
    });
    return new Promise(async (resolve) => {
      try {
        const assetNode = this.getAssetNode(data);
        if (assetNode.length) {
          resolve(assetNode);
          return;
        }

        let { url, materialUrl } = data.details;
        let component: any;

        const modelNode = await this.scene.createNode();
        if (data.details.fileName && url) {
          const date = (data.updatedAt as unknown as Date) || new Date();
          url = this.getMeshUrl(url, date);
          materialUrl = materialUrl && this.getMeshUrl(materialUrl, date);
        }

        //get outline pass from effect composer

        const hasAction =
          data.details?.products?.length > 0 || data?.actionType !== TourActionType.None;

        if (data.details.isGLTF) {
          if (!url.match(/\.(glb)|\.(gltf)/g)) {
            throw new Error(`Object ${data.details.title} is not GLTF format`);
          }

          component = modelNode.addComponent(gltfLoaderComponentType, {
            url,
            animationEnabled: data.animated,
            animationTriggerEnabled: data.toursVSImages && data.toursVSImages.length > 0,
            audioEnabled: data.audioEnabled,
            audioSrc: data.audioSrc,
            enableHoverAnimation: data.enableHoverAnimation,
            hideOnAnimationFinished: data.hideOnAnimationFinished,
            enableIdleAnimation: data.enableIdleAnimation,
            adminPanelAccess: this.config.adminPanelAccess,
            animationLoop: data.loop,
            scale: { x: data.scaleX, y: data.scaleY, z: data.scaleZ },
            colliderEnabled: enableCollider || hasAction,
          });
        } else {
          if (!url.match(/\.(obj)/g)) {
            throw new Error(`Object ${data.details.title} is not OBJ format`);
          }
          component = modelNode.addComponent(objLoaderComponentType, {
            url,
            materialUrl,
            onLoaded: () => {
              // Creating an optimised box collider for Public if it's needed
              if (this.config.adminPanelAccess || !data.enableHoverAnimation) return;

              try {
                const geometry = CachedGeometry.CreateColliderMeshBoundingBoxFromScene(
                  this.three,
                  component.outputs.objectRoot.children[0],
                );
                const material = new this.three.MeshBasicMaterial({
                  color: 'white',
                  transparent: true,
                  opacity: 0,
                });
                const collider = new this.three.Mesh(geometry, material);

                const b = new this.three.Box3();
                b.setFromObject(component.outputs.objectRoot.children[0]);
                const modelCenter = new this.three.Vector3();
                b.getCenter(modelCenter);

                const b2 = new this.three.Box3();
                b2.setFromObject(collider);
                const colliderCenter = new this.three.Vector3();
                b2.getCenter(colliderCenter);

                collider.position.set(
                  modelCenter.x - colliderCenter.x,
                  modelCenter.y - colliderCenter.y,
                  modelCenter.z - colliderCenter.z,
                );

                component.outputs.objectRoot.add(collider);
                component.outputs.collider = collider;
              } catch (e) {
                console.log('[Err] No collider for ', component);
              }
            },
          });

          component.outputs.onPropertyChanged('objectRoot', (root: THREE.Object3D) => {
            if (!root) return;
          });

          // subscribe to the outputs.objectRoot property change which triggers when the OBJ is loaded and ready.
          component.outputs.onPropertyChanged('objectRoot', (root: THREE.Object3D) => {
            if (!root) return;

            root.traverse((obj: THREE.Object3D) => {
              if (obj.type !== 'Mesh') return;
              const mesh = obj as THREE.Mesh;

              if (Array.isArray(mesh.material)) {
                mesh.material.forEach((material: any) => meshMaterialChange(material));
              } else {
                mesh.material = meshMaterialChange(mesh.material);
              }

              mesh.castShadow = true; // default is false
              mesh.receiveShadow = true; // default is false
            });
          });
        }

        this.initObjectEvents(component, data);

        modelNode.position.set(data.positionX, data.positionY, data.positionZ);
        modelNode.obj3D.rotation.set(data.rotationX, data.rotationY, data.rotationZ);
        modelNode.obj3D.scale.set(data.scaleX, data.scaleY, data.scaleZ);
        // const THREE=ThreeInstance.get();
        // let factor:number=0.001;
        // modelNode.position?.set(data.positionX * factor, data.positionY* factor, data.positionZ * factor);
        // modelNode.quaternion?.set(new THREE.Quaternion().setFromAxisAngle( new THREE.Vector3( data.rotationX?data.rotationX * factor:0,
        //   data.rotationY?data.rotationY * factor:0,
        //   data.rotationZ?data.rotationZ * factor:0)));
        // modelNode.scale?.set(data.scaleX * factor, data.scaleY * factor, data.scaleZ * factor);
        // 3D object event listener
        modelNode.start();

        component.outputs.onPropertyChanged('loadingState', async (state: string) => {
          if (state === LoadingState.Loaded) {
            // waiting for all textures to load
            this.assets.push([data, [modelNode, component]]);
            // immediately change mode to 'edit' for the new asset
            if (!data.id) {
              await this.changeMode(SceneMode.Edit, [modelNode, component]);
              // position of the new asset is the camera position
              // move 1 meter out from the user camera position
              modelNode.position.setX(modelNode.position.x + 1);
            }

            resolve([modelNode, component]);
          }
          if (state === LoadingState.Error) {
            resolve([] as any);
          }
        });
      } catch (error) {
        logger.error(error);
        resolve([] as any);
      }
    });
  }

  initObjectEvents(component, data) {
    let outlinePass = null as any;
    if (this.effectComposer) {
      // Only outlinePass has this property
      // Search by name no longer works since SDK instance of THREE.JS has tweaked class names
      const outlinePassIndex = this.effectComposer.passes.findIndex((pass) => pass.edgeThickness);
      outlinePass = this.effectComposer.passes[outlinePassIndex];
    }

    component.onEvent = async (type: VSInteractionType, eventData: any) => {
      const rootMesh = component.outputs.objectRoot;
      if (type === VSInteractionType.HOVER) {
        if (outlinePass && data.enableHoverAnimation) {
          if (eventData.hover) {
            if (rootMesh.children.length)
              rootMesh.children.forEach((obj) => {
                if (obj.uuid !== component.outputs.collider.uuid) this.outlinedMeshes.push(obj);
              });
          } else {
            this.outlinedMeshes.forEach((mesh, index) => {
              if (rootMesh.children.length)
                rootMesh.children.forEach((obj) => {
                  if (mesh.uuid === obj.uuid) this.outlinedMeshes.splice(index, 1);
                });
            });
          }
          if (this.outlinedMeshes.length)
            outlinePass.selectedObjects = [this.outlinedMeshes.at(-1)];
          else outlinePass.selectedObjects = [];
          if (this.outlinedMeshes.length) this.mpSDK.Pointer.setVisible(false);
          else this.mpSDK.Pointer.setVisible(true);
        }
        this.emit(NodeEventsTypes.OBJECT_HOVER, {
          assetId: data.id,
          eventData,
          data,
        });
        this.emit(NodeEventsTypes.OBJECT_HOVER, { assetId: data.id, eventData, data });
      }
      if (type === VSInteractionType.CLICK) {
        this.emit(NodeEventsTypes.OBJECT_CLICK, { assetId: data.id, eventData, data });
      }
    };
  }

  async remove(data: TourObject) {
    const node = this.getAssetNode(data);
    console.log('REMOVING', node);
    if (node) {
      const [modelNode] = node;
      if (!modelNode) return;
      modelNode.stop();

      // cleaning up the assets array
      this.assets = this.filterAssetOut(data);
    }
  }

  async moveTo(data: TourObject) {
    if (this.getAssetNode(data) && data.sweep_uuid) {
      await this.mpSDK.Sweep.moveTo(data.sweep_uuid, {
        rotation: {
          x: data.srX,
          y: data.srY,
          z: data.srZ,
        },
        transition: this.mpSDK.Sweep.Transition.INSTANT,
      });
    }
  }

  private getMeshUrl = (url: string, updatedAt: Date) =>
    url.startsWith('http') ? url : `${this.config.appAws}${url}${getVersion(updatedAt)}`;

  async changeMode(
    mode: SceneMode,
    node: TSceneNode,
    transformationMode: TransformationMode = TransformationMode.TRANSLATE,
  ) {
    const [modelNode] = node;

    if (mode === SceneMode.Edit) {
      // Make the transform control visible so that the user can manipulate the control selection.
      this.transformControls.control.outputs.objectRoot.visible = true;
      // The three.js object being controlled by this component.
      this.transformControls.control.inputs.selection = modelNode;
      // The transformation mode. Valid values are ‘translate’, ‘rotate’ and ‘scale’.
      this.transformControls.control.inputs.mode = transformationMode;
    } else {
      this.transformControls.control.outputs.objectRoot.visible = false;
      this.transformControls.control.inputs.selection = null;
      this.transformControls.control.inputs.mode = transformationMode;
    }
  }

  getAssetNode = (asset: any): TSceneNode => {
    let node = [] as any;
    this.assets.forEach(([a, n]) => {
      if ((a.id && a.id === asset.id) || (a.uuid === asset.uuid && a.uuid && asset.uuid)) {
        node = n;
      }
    });
    return node;
  };

  filterAssetOut = (asset: any): Array<[any, any[]]> => {
    return this.assets.filter(
      ([a]) => !((a.id && a.id === asset.id) || (a.uuid === asset.uuid && a.uuid && asset.uuid)),
    );
  };
}

export default SceneObject;
